import { useState } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

export type OtpChannel = 'email' | 'mobile';

interface SentTo {
  channel: OtpChannel;
  value: string;
}

/**
 * Shared registration-OTP flow for Member (Auth.tsx) and Patient
 * (PatientAuthModal.tsx) signup — both need the exact same channel-priority
 * rule (product decision, 2026-08-12): if an email was given, verify email;
 * only fall back to mobile OTP when there's no email at all, even if a
 * mobile number was also given. Kept in one hook so that rule lives in
 * exactly one place instead of being re-implemented per form.
 *
 * `purpose` scopes the OTP row (and its own rate limit) — use a distinct
 * value per registration flow (e.g. 'member_register', 'patient_register')
 * so they never share attempts/rate-limit state with each other or with
 * unrelated OTP uses like AIChatWidget's 'ai_chat_verify'.
 */
export function useRegistrationOtp(purpose: string) {
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [sentTo, setSentTo] = useState<SentTo | null>(null);

  const sendOtp = async (email?: string, phone?: string): Promise<{ success: boolean; channel: OtpChannel; message?: string; devOtp?: string }> => {
    const trimmedEmail = (email || '').trim();
    const trimmedPhone = (phone || '').trim();
    const channel: OtpChannel = trimmedEmail ? 'email' : 'mobile';
    const value = channel === 'email' ? trimmedEmail : trimmedPhone;

    if (!value) {
      return { success: false, channel, message: 'Email or mobile number is required' };
    }

    setSending(true);
    setVerified(false);
    try {
      const res = channel === 'email'
        ? await bridgeApi.post<any>('send_email_otp', { email: value, purpose })
        : await bridgeApi.post<any>('send_mobile_otp', { phone: value, purpose });
      setSentTo({ channel, value });
      return { success: true, channel, message: res?.message, devOtp: res?.dev_otp };
    } catch (e: any) {
      return { success: false, channel, message: e?.message || 'Failed to send verification code' };
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async (otp: string): Promise<{ success: boolean; message?: string }> => {
    if (!sentTo) {
      return { success: false, message: 'Request a verification code first' };
    }
    setVerifying(true);
    try {
      const res = sentTo.channel === 'email'
        ? await bridgeApi.post<any>('verify_email_otp', { email: sentTo.value, otp, purpose })
        : await bridgeApi.post<any>('verify_mobile_otp', { phone: sentTo.value, otp, purpose });
      setVerified(true);
      return { success: true, message: res?.message };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Incorrect or expired code' };
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    setSentTo(null);
    setVerified(false);
  };

  return { sendOtp, verifyOtp, reset, sending, verifying, verified, sentTo };
}
